/**
 * Shared font configuration used by CMS fields and theme system.
 *
 * Font files are self-hosted in public/fonts/ (downloaded via scripts/download-fonts.mjs).
 * The `value` is the font family name matching the directory in public/fonts/.
 */

export type FontCategory = "sans-serif" | "serif" | "display";

export interface FontOption {
    label: string;
    value: string;
    category: FontCategory;
}

/** Fonts available for body text (sans-serif focused) */
export const BODY_FONTS: FontOption[] = [
    // Sans-serif
    { label: "Inter", value: "Inter", category: "sans-serif" },
    { label: "Roboto", value: "Roboto", category: "sans-serif" },
    { label: "Open Sans", value: "Open Sans", category: "sans-serif" },
    { label: "Poppins", value: "Poppins", category: "sans-serif" },
    { label: "Montserrat", value: "Montserrat", category: "sans-serif" },
    { label: "Ubuntu", value: "Ubuntu", category: "sans-serif" },
    { label: "Lato", value: "Lato", category: "sans-serif" },
    { label: "Nunito", value: "Nunito", category: "sans-serif" },
    { label: "Raleway", value: "Raleway", category: "sans-serif" },
    { label: "Source Sans 3", value: "Source Sans 3", category: "sans-serif" },
    { label: "PT Sans", value: "PT Sans", category: "sans-serif" },
    { label: "Noto Sans", value: "Noto Sans", category: "sans-serif" },
    { label: "Work Sans", value: "Work Sans", category: "sans-serif" },
    { label: "DM Sans", value: "DM Sans", category: "sans-serif" },
    { label: "Barlow", value: "Barlow", category: "sans-serif" },
    { label: "Rubik", value: "Rubik", category: "sans-serif" },
    { label: "Quicksand", value: "Quicksand", category: "sans-serif" },
    { label: "Outfit", value: "Outfit", category: "sans-serif" },
    { label: "Plus Jakarta Sans", value: "Plus Jakarta Sans", category: "sans-serif" },
    { label: "Manrope", value: "Manrope", category: "sans-serif" },
    { label: "Figtree", value: "Figtree", category: "sans-serif" },
    { label: "Cabin", value: "Cabin", category: "sans-serif" },
    { label: "Karla", value: "Karla", category: "sans-serif" },
    { label: "Mulish", value: "Mulish", category: "sans-serif" },
    { label: "Lexend", value: "Lexend", category: "sans-serif" },
    { label: "IBM Plex Sans", value: "IBM Plex Sans", category: "sans-serif" },
    { label: "Exo 2", value: "Exo 2", category: "sans-serif" },
    { label: "Titillium Web", value: "Titillium Web", category: "sans-serif" },
    { label: "Archivo", value: "Archivo", category: "sans-serif" },
    { label: "Overpass", value: "Overpass", category: "sans-serif" },
];

/** Additional fonts available only for headings (includes serif & display) */
export const HEADING_ONLY_FONTS: FontOption[] = [
    { label: "Playfair Display", value: "Playfair Display", category: "serif" },
    { label: "Merriweather", value: "Merriweather", category: "serif" },
    { label: "Lora", value: "Lora", category: "serif" },
    { label: "PT Serif", value: "PT Serif", category: "serif" },
    { label: "Oswald", value: "Oswald", category: "display" },
    { label: "Roboto Condensed", value: "Roboto Condensed", category: "display" },
    { label: "Bebas Neue", value: "Bebas Neue", category: "display" },
    { label: "Anton", value: "Anton", category: "display" },
    { label: "Righteous", value: "Righteous", category: "display" },
    { label: "Teko", value: "Teko", category: "display" },
];

/** All fonts available for headings = body fonts + heading-only fonts */
export const HEADING_FONTS: FontOption[] = [...BODY_FONTS, ...HEADING_ONLY_FONTS];

/** All unique font families (for preloading / font-face) */
export const ALL_FONTS: FontOption[] = [...BODY_FONTS, ...HEADING_ONLY_FONTS];

/** Get the CSS font-stack suffix for a font */
export function getFontStack(fontName: string): string {
    const font = ALL_FONTS.find((f) => f.value === fontName);
    if (!font) return "sans-serif";
    if (font.category === "serif") return "serif";
    return "sans-serif";
}


